import {
  type DocUploadResponse,
  type ErrorResponse,
  type NewVerificationRequest,
  type StudentPersonalInfoResponse,
  type TeacherPersonalInfoResponse,
  type MemberPersonalInfoResponse,
  type PendingResponse,
  type StudentPersonalInfoViewModel,
  type TeacherPersonalInfoViewModel,
  type MemberPersonalInfoViewModel,
  type SuccessResponse,
  type DocUploadViewModel,
  type ActiveMilitaryPersonalInfoViewModel,
  type ActiveMilitaryPersonalInfoResponse,
  type InactiveMilitaryPersonalInfoResponse,
  type InactiveMilitaryPersonalInfoViewModel,
  type SeniorPersonalInfoResponse,
  type SeniorPersonalInfoViewModel,
  type MilitaryStatusResponse,
  type SSOResponse,
  type SMSLoopResponse,
  type FirstResponderPersonalInfoResponse,
  type FirstResponderPersonalInfoViewModel,
  type EmploymentPersonalInfoResponse,
  type EmploymentPersonalInfoViewModel,
  type ProgramTheme,
  type SocialSecurityResponse,
  type SocialSecurityViewModel,
  type AgePersonalInfoResponse,
  type MedicalProfessionalPersonalInfoResponse,
  type VerificationStepMap,
  type AllResponseTypes,
  type EmailLoopResponse,
  type DriverLicensePersonalInfoResponse,
  type GeneralIdentityPersonalInfoResponse,
  type HybridIdentityPersonalInfoResponse,
  type LicensedProfessionalPersonalInfoViewModel,
  type LicensedProfessionalPersonalInfoResponse,
  type MoverPersonalInfoViewModel,
  type MoverPersonalInfoResponse,
  type LowIncomePersonalInfoViewModel,
  type LowIncomePersonalInfoResponse,
  type ConsolationResponse,
  type OverrideResponse,
  type IDCheckLoopResponse,
  type CollectIdentifierResponse,
  type CollectPersonalInfoResponse,
  type CompleteAuthenticationResponse,
  type ThirdPartyLoopResponse,
} from "../lib/types/types";
import { employmentPInfoReqEmpty, socialSecurityEmpty } from "../lib/types/empties";
import {
  IdCheckLoopServiceType,
  RewardDisplayEnum,
  ThirdPartyLoopProvider,
} from "../lib/types/runtimeTypes";

// ===================== New Verification ====================
export const mockNewRequest: NewVerificationRequest = {
  programId: "5bbd127d9781852f68e14ddc",
};

// ===================== Student Personal Info ====================

export const mockStudentPersonalInfoResponse: StudentPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectStudentPersonalInfo",
  segment: "student",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockStudentPersonalInfoViewModel: StudentPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  birthDate: "", // ISO-8601 'YYYY-MM-DD'
  organization: {
    id: 0,
    name: "",
  },
  countryChoice: undefined,
  localeChoice: {
    value: undefined,
    label: undefined,
  },
};

// ===================== Teacher Personal Info ====================

export const mockTeacherPersonalInfoResponse: TeacherPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectTeacherPersonalInfo",
  segment: "teacher",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockTeacherPersonalInfoViewModel: TeacherPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Member Personal Info ====================

export const mockMemberPersonalInfoResponse: MemberPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectMemberPersonalInfo",
  segment: "member",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};
export const mockMemberPersonalInfoViewModel: MemberPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  birthDate: "",
  email: "",
  phoneNumber: "",
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Senior Personal Info ====================

export const mockSeniorPersonalInfoResponse: SeniorPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectSeniorPersonalInfo",
  segment: "senior",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// ===================== Age Personal Info ====================

export const mockAgePersonalInfoResponse: AgePersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectAgePersonalInfo",
  segment: "age",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
  minAge: 55,
  maxAge: 120,
};

// Continue the verification process for a given verificationId
export const mockSeniorPersonalInfoViewModel: SeniorPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  birthDate: "",
  postalCode: "",
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Military Status ====================
export const mockMilitaryStatusResponse: MilitaryStatusResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectMilitaryStatus",
  segment: "military",
  subSegment: null,
  submissionUrl: "",
  availableStatuses: ["ACTIVE_DUTY", "VETERAN"],
  locale: "en-US",
};

// ===================== Active Military Personal Info ====================

export const mockActiveMilitaryPersonalInfoResponse: ActiveMilitaryPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectActiveMilitaryPersonalInfo",
  segment: "military",
  subSegment: "activeDuty",
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockActiveMilitaryPersonalInfoViewModel: ActiveMilitaryPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  birthDate: "", // ISO-8601 'YYYY-MM-DD'
  status: undefined,
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Inactive Military Personal Info ====================

export const mockInactiveMilitaryPersonalInfoResponse: InactiveMilitaryPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectInactiveMilitaryPersonalInfo",
  segment: "military",
  subSegment: "veteran",
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockInactiveMilitaryPersonalInfoViewModel: InactiveMilitaryPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  birthDate: "", // ISO-8601 'YYYY-MM-DD'
  status: undefined,
  dischargeDate: "", // ISO-8601 'YYYY-MM-DD'
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== First Responder Personal Info ====================

export const mockFirstResponderPersonalInfoResponse: FirstResponderPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectFirstResponderPersonalInfo",
  segment: "firstResponder",
  subSegment: "emt",
  submissionUrl: "",
  availableStatuses: ["FIREFIGHTER", "POLICE"],
  instantMatchAttempts: 0,
  locale: "en-US",
};

export const mockMedicalProfessionalPersonalInfoResponse: MedicalProfessionalPersonalInfoResponse =
  {
    verificationId: "5a3bffd5e4b05f0b878a9762",
    currentStep: "collectMedicalProfessionalPersonalInfo",
    segment: "medical",
    subSegment: "nurse",
    submissionUrl: "",
    availableStatuses: ["NURSE", "DOCTOR"],
    instantMatchAttempts: 0,
    locale: "en-US",
  };

// Continue the verification process for a given verificationId
export const mockFirstResponderPersonalInfoViewModel: FirstResponderPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  birthDate: "", // ISO-8601 'YYYY-MM-DD'
  status: undefined,
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Employment Personal Info ====================

export const mockEmploymentPersonalInfoResponse: EmploymentPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectEmployeePersonalInfo",
  segment: "employment",
  subSegment: null,
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockEmploymentPersonalInfoViewModel: EmploymentPersonalInfoViewModel =
  employmentPInfoReqEmpty;

// ===================== Identity Personal Info =========================

export const mockDriverLicensePersonalInfoResponse: DriverLicensePersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectDriverLicensePersonalInfo",
  segment: "identity",
  subSegment: "driverLicense",
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

export const mockGeneralIdentityPersonalInfoResponse: GeneralIdentityPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectGeneralIdentityPersonalInfo",
  segment: "identity",
  subSegment: "generalIdentity",
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

export const mockHybridIdentityPersonalInfoResponse: HybridIdentityPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectHybridIdentityPersonalInfo",
  segment: "identity",
  subSegment: "generalIdentity",
  submissionUrl: "",
  instantMatchAttempts: 0,
  locale: "en-US",
};

// ===================== Professionals Personal Info =========================

export const mockLicensedProfessionalPersonalInfoResponse: LicensedProfessionalPersonalInfoResponse =
  {
    verificationId: "5a3bffd5e4b05f0b878a9761",
    currentStep: "collectLicensedProfessionalPersonalInfo",
    segment: "licensedProfessional",
    submissionUrl: "",
    subSegment: "licensedCosmetologists",
    instantMatchAttempts: 0,
    locale: "en-US",
  };

export const mockLicensedProfessionalPersonalInfoViewModel: LicensedProfessionalPersonalInfoViewModel =
  {
    firstName: "",
    lastName: "",
    birthDate: "",
    postalCode: "",
    email: "",
    statuses: ["LICENSED_COSMETOLOGIST"],
    organization: {
      id: 0,
      name: "",
    },
    localeChoice: {
      value: undefined,
      label: undefined,
    },
    countryChoice: undefined,
  };

// ===================== Movers Personal Info =========================

export const mockMoverPersonalInfoResponse: MoverPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9761",
  currentStep: "collectMoverPersonalInfo",
  segment: "recentMover",
  submissionUrl: "",
  subSegment: "homeBuyer",
  instantMatchAttempts: 0,
  locale: "en-US",
};

export const mockMoverPersonalInfoViewModel: MoverPersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  address1: "",
  postalCode: "",
  email: "",
  statuses: ["HOME_BUYER", "OTHER_MOVER"],
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Collect Identifier =========================

export const mockCollectIdentifierResponse: CollectIdentifierResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9768",
  currentStep: "collectIdentifier",
  submissionUrl: "",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};

// ===================== Collect Personal Info =========================

export const mockCollectPersonalInfoResponse: CollectPersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9769",
  currentStep: "collectPersonalInfo",
  submissionUrl: "",
  segment: "student",
  subSegment: null,
  locale: "en-US",
  availableStatuses: [],
};

// ===================== Complete Authentication =========================

export const mockCompleteAuthenticationResponse: CompleteAuthenticationResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9760",
  currentStep: "completeAuthentication",
  submissionUrl: "",
  cancelUrl: "",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};

// ===================== Low Income Personal Info =========================

export const mockLowIncomePersonalInfoResponse: LowIncomePersonalInfoResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9761",
  currentStep: "collectLowIncomePersonalInfo",
  segment: "lowIncome",
  submissionUrl: "",
  subSegment: "snapBenefits",
  instantMatchAttempts: 0,
  locale: "en-US",
};

export const mockLowIncomePersonalInfoViewModel: LowIncomePersonalInfoViewModel = {
  firstName: "",
  lastName: "",
  birthDate: "",
  address1: "",
  city: "",
  state: "",
  postalCode: "",
  email: "",
  statuses: ["SNAP_BENEFITS", "OTHER_GOVERNMENT_ASSISTANCE"],
  organization: {
    id: 0,
    name: "",
  },
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ===================== Social Security Number step ====================
export const mockSocialSecurityResponse: SocialSecurityResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "collectSocialSecurityNumber",
  segment: "military",
  subSegment: null,
  submissionUrl: "",
  cancelUrl: "",
  locale: "en-US",
};

export const mockSocialSecurityViewModel: SocialSecurityViewModel = socialSecurityEmpty;

// ======================= Step Doc Upload =====================

export const mockDocUploadVerificationResponse: DocUploadResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "docUpload",
  segment: "student",
  subSegment: null,
  submissionUrl: "",
  locale: "en-US",
};

// Continue the verification process for a given verificationId
export const mockDocUploadViewModel: DocUploadViewModel = {
  file1: null,
  file2: null,
  localeChoice: {
    value: undefined,
    label: undefined,
  },
  countryChoice: undefined,
};

// ========================= "Step" Success =======================

export const mockSuccessResponse: SuccessResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "success",
  segment: "student",
  subSegment: null,
  rewardCode: "MY_REWARD_123",
  locale: "en-US",
};
// There is no request associated with this step

// ========================= "Step" Consolation =======================

export const mockConsolationResponse: ConsolationResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "consolation",
  segment: "student",
  subSegment: null,
  consolationRewardCode: "MY_REWARD_123",
  locale: "en-US",
};
// There is no request associated with this step

// ========================= "Step" Override =======================

export const mockOverrideResponse: OverrideResponse = {
  verificationId: "635a9e342f69032c316e05f9",
  currentStep: "override",
  errorIds: ["invalidOverrideCode"],
  segment: "medical",
  subSegment: "nurse",
  submissionUrl: "",
  locale: "en-US",
  country: "US",
};
// There is no request associated with this step

// ============================ "Step" Error ===========================

export const mockErrorResponse: ErrorResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "error",
  segment: "student",
  subSegment: null,
  redirectUrl: "https://sheerid.com",
  errorIds: ["internalServerError"],
  locale: "en-US",
};
// There is no request associated with this step

// This is a faked response to show the missingRequiredMetadata view.
// It's triggered as a fake step that loads an error step override.
export const mockMissingMetadataResponse: ErrorResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "error",
  segment: "student",
  subSegment: null,
  redirectUrl: "https://sheerid.com",
  errorIds: ["missingRequiredMetadata"],
  locale: "en-US",
};

// This is a faked response to show the missingRequiredExternalId view.
// It's triggered as a fake step that loads an error step override.
export const mockMissingExternalIdResponse: ErrorResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "error",
  segment: "student",
  subSegment: null,
  redirectUrl: "https://sheerid.com",
  errorIds: ["missingRequiredExternalId"],
  locale: "en-US",
};

// ============================ "Step" Under Review ===========================

export const mockPendingResponse: PendingResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "pending",
  statusUrl: "",
  segment: "student",
  subSegment: null,
  locale: "en-US",
  maxReviewTime: "A_HALF_HOUR",
  estimatedReviewTime: "A_FEW_MINUTES",
};
// There is no request associated with this step

// ======================== "Step" Doc Review Limit Exceeded =======================

export const mockDocReviewLimitExceededResponse: ErrorResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  currentStep: "error",
  segment: "student",
  subSegment: null,
  redirectUrl: null,
  errorIds: ["docReviewLimitExceeded"],
  locale: "en-US",
};
// There is no request associated with this step

// ======================== "Step" SSO =======================

export const mockSSOResponse: SSOResponse = {
  verificationId: "5a3bffd5e4b05f0b878a9762",
  loginUrl: "https://sheerid.com/ssoLoginUrl",
  cancelUrl: "cancelUrl",
  currentStep: "sso",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};
// There is no request associated with this step

// ======================== "Step" Third Party Loop =======================

export const mockThirdPartyResponse: ThirdPartyLoopResponse = {
  verificationId: "6733d1f6c75d2c30e481f36c",
  loginUrl: "loginUrl",
  cancelUrl: "cancelUrl",
  currentStep: "thirdPartyLoop",
  segment: "age",
  subSegment: null,
  locale: "en-US",
  thirdPartyLoopProvider: ThirdPartyLoopProvider.SIGNZY,
};
// There is no request associated with this step

// ======================== "Step" SMSLoop =======================

export const mockSMSLoopResponse: SMSLoopResponse = {
  verificationId: "5e2f0bd2d7121162bb849f17",
  submissionUrl: "",
  currentStep: "smsLoop",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};

export const mockEmailLoopResponse: EmailLoopResponse = {
  verificationId: "5e2f0bd2d7121162bb849f17",
  submissionUrl: "https://foo.bar",
  currentStep: "emailLoop",
  currentState: "pending",
  segment: "student",
  subSegment: null,
  locale: "en-US",
  canResendEmailLoop: true,
  cancelUrl: "https://foo.bar",
};

export const mockEmailLoopCollectOrganizationEmailResponse: EmailLoopResponse = {
  verificationId: "5e2f0bd2d7121162bb849f17",
  submissionUrl: "https://foo.bar",
  currentStep: "emailLoop",
  currentState: "collectOrganizationEmail",
  segment: "student",
  subSegment: null,
  locale: "en-US",
  canResendEmailLoop: true,
  cancelUrl: "https://foo.bar",
};

export const mockCancelEmailLoopResponse: DocUploadResponse = {
  verificationId: "5e2f0bd2d7121162bb849f17",
  submissionUrl: "",
  currentStep: "docUpload",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};

export const mockIDCheckLoopResponse: IDCheckLoopResponse = {
  verificationId: "5e2f0bd2d7121162bb849f17",
  submissionUrl: "",
  sdkToken: null,
  currentStep: "idCheckLoop",
  segment: "student",
  subSegment: null,
  locale: "en-US",
  idCheckServiceType: IdCheckLoopServiceType.ID_CHECK_LIVENESS,
};

export const mockCancelDocUploadResponse: OverrideResponse = {
  submissionUrl: "",
  verificationId: "63a22b25e7501047aa05bc3a",
  currentStep: "override",
  segment: "student",
  subSegment: null,
  locale: "en-US",
};

export const allMockedResponses: VerificationStepMap<AllResponseTypes> = {
  collectStudentPersonalInfo: mockStudentPersonalInfoResponse,
  collectTeacherPersonalInfo: mockTeacherPersonalInfoResponse,
  collectMemberPersonalInfo: mockMemberPersonalInfoResponse,
  docUpload: mockDocUploadVerificationResponse,
  success: mockSuccessResponse,
  consolation: mockConsolationResponse,
  override: mockOverrideResponse,
  error: mockErrorResponse,
  pending: mockPendingResponse,
  docReviewLimitExceeded: mockDocReviewLimitExceededResponse,
  collectMilitaryStatus: mockMilitaryStatusResponse,
  collectActiveMilitaryPersonalInfo: mockActiveMilitaryPersonalInfoResponse,
  collectInactiveMilitaryPersonalInfo: mockInactiveMilitaryPersonalInfoResponse,
  collectSeniorPersonalInfo: mockSeniorPersonalInfoResponse,
  collectFirstResponderPersonalInfo: mockFirstResponderPersonalInfoResponse,
  collectEmployeePersonalInfo: mockEmploymentPersonalInfoResponse,
  collectDriverLicensePersonalInfo: mockDriverLicensePersonalInfoResponse,
  collectGeneralIdentityPersonalInfo: mockGeneralIdentityPersonalInfoResponse,
  collectHybridIdentityPersonalInfo: mockHybridIdentityPersonalInfoResponse,
  collectLicensedProfessionalPersonalInfo: mockLicensedProfessionalPersonalInfoResponse,
  collectMoverPersonalInfo: mockMoverPersonalInfoResponse,
  collectLowIncomePersonalInfo: mockLowIncomePersonalInfoResponse,
  collectIdentifier: mockCollectIdentifierResponse,
  collectPersonalInfo: mockCollectPersonalInfoResponse,
  completeAuthentication: mockCompleteAuthenticationResponse,
  sso: mockSSOResponse,
  smsLoop: mockSMSLoopResponse,
  emailLoop: mockEmailLoopResponse,
  emailLoopCollectOrganizationEmail: mockEmailLoopCollectOrganizationEmailResponse,
  cancelEmailLoop: mockCancelEmailLoopResponse,
  idCheckLoop: mockIDCheckLoopResponse,
  collectSocialSecurityNumber: mockSocialSecurityResponse,
  collectAgePersonalInfo: mockAgePersonalInfoResponse,
  collectMedicalProfessionalPersonalInfo: mockMedicalProfessionalPersonalInfoResponse,
  cancelSocialSecurityNumber: {} as AllResponseTypes,
  cancelDocUpload: mockCancelDocUploadResponse,
  missingRequiredMetadata: mockMissingMetadataResponse,
  missingRequiredExternalId: mockMissingExternalIdResponse,
  thirdPartyLoop: mockThirdPartyResponse,
};

// ======================= Student Theme =======================
export const mockInternationalStudentTheme: ProgramTheme = {
  isTestMode: true,
  isSmsNotifierConfigured: false,
  intl: {
    locale: "en-US",
    messages: {
      errorId: {
        verificationLimitExceeded: "exceeded",
        noRemainingRewardCodes: "no codes",
      },
      lowRewardPool: null,
      companyName: "sheerid",
      step: {
        personalInfo: {
          title: "Unlock this Student-Only Offer",
        },
        docUpload: {
          title: "We need your ID & class schedule",
        },
        success: {
          title: "Success! You've been verified",
          subtitle:
            "Here is your personal coupon code. It is unique to you and can only be used once.",
          redirectUrl: null,
          redirectButtonText: "Use code now",
        },
        error: {
          errorId: {
            verificationLimitExceeded: {
              title: "exceeded",
            },
            noRemainingRewardCodes: {
              title: "no codes",
              buttonText: "find offer",
            },
            expiredProgram: {
              title: "no codes",
              buttonText: "find offer",
            },
            inactiveProgram: {
              title: "no codes",
              buttonText: "find offer",
            },
            missingRequiredMetadata: {
              title: "missing required metadata",
            },
            missingRequiredExternalId: {
              title: "missing required external id",
            },
          },
        },
      },
    },
  },
  customCss:
    "/* \r\n\r\n**********************\r\nATTENTION - This configuration is written by sheerid-web-apps, my.sheerid.com\r\n**********************\r\n\r\n */",
  logoUrl: null,
  openOrgSearchEnabled: false,
  config: {
    countries: ["US", "CA", "GB"],
    locales: ["en-US"],
    maxReviewTime: "20_MIN",
    estimatedReviewTime: "A_FEW_MINUTES",
    marketConsent: {
      enabled: null,
      required: null,
      message: null,
    },
    customMetadata: {
      enabled: false,
      keys: [],
      requiredKeys: [],
    },
    customFaqLink: null,
    orgSearchUrl:
      "https://orgsearch-preview.sheerid.net/rest/organization/search?tags=TITLE4%2CHEI&country=US%2CCA%2CGB%2CJP%2CMX%2CFR%2CDE%2CZW&type=UNIVERSITY&name=",
    orgTypes: ["UNIVERSITY"],
    excludedOrganizationIds: [],
    offerType: "staticCode",
    minAge: null,
    maxAge: null,
    rewardDisplay: [RewardDisplayEnum.TEXT],
  },
  themeChoices: {
    font: "Arial",
    primaryFontColor: "#2d2d2d",
    h1FontColor: "#2d2d2d",
    helperFontColor: "#2d2d2d",
    backgroundColor: "#fff",
    buttonColor: "#0077c8",
    linkColor: "#0077c8",
    buttonFontColor: "#fff",
    logoUrl: null,
    customCss:
      "/* \r\n\r\n**********************\r\nATTENTION - This configuration is written by sheerid-web-apps, my.sheerid.com\r\n**********************\r\n\r\n */",
    landingPage: {
      primaryFontColor: "#2d2d2d",
      backgroundColor: "#fff",
      backgroundImageUrl: null,
      layout: "center",
      logoUrl: null,
    },
  },
};
